import { Component } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";
import { InputComponent } from "../../../shared/input/input.component";
import { ButtonComponent } from "../../../shared/button/button.component";
import { RouterLink } from "@angular/router";
import { LayoutComponent } from "../../../shared/layout/layout.component";
import { TitleComponent } from "../../../shared/title/title.component";
import { GigyaService } from "../../../services/gigya.service";
import { SpinnerComponent } from "../../../shared/icons/spinner/spinner.component";
import { BreadcrumbComponent } from "../../../shared/breadcrumb/breadcrumb.component";
import { PasswordService, PasswordApiError } from "../../../services/password.service";

@Component({
  selector: "app-update-password",
  templateUrl: "./update-password.component.html",
  styleUrl: "./update-password.component.css",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputComponent,
    ButtonComponent,
    RouterLink,
    LayoutComponent,
    TitleComponent,
    BreadcrumbComponent,
    SpinnerComponent,
  ],
})
export class UpdatePasswordComponent {
  passwordForm: FormGroup;
  validations = { minLength: false, uppercase: false, number: false, specialChar: false };
  button_clicked = false;
  submitting = false;
  // wrongPasswordError is tracked separately because it resets on currentPassword changes,
  // not newPassword changes like the other errors.
  wrongPasswordError = false;
  apiError: PasswordApiError | null = null;
  success = false;
  setPassword = false;
  regToken = "";

  // In setAccountInfo, 400006 means "same as current password", not "too weak".
  private readonly errorOverrides = new Map<number, string>([
    [400006, "updatePassword.samePasswordError"],
  ]);

  constructor(
    private fb: FormBuilder,
    private gigyaService: GigyaService,
    private passwordService: PasswordService,
  ) {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("regToken")) {
      this.setPassword = true;
      this.regToken = searchParams.get("regToken") || "";
    }

    this.passwordForm = this.fb.group({
      currentPassword: ["", this.setPassword ? [] : [Validators.required]],
      newPassword: ["", [Validators.required, this.passwordService.validator()]],
      confirmPassword: ["", [Validators.required]],
    });

    this.passwordForm.get("currentPassword")?.valueChanges.subscribe(() => {
      if (this.wrongPasswordError) {
        this.button_clicked = false;
        this.wrongPasswordError = false;
      }
    });

    this.passwordForm.get("newPassword")?.valueChanges.subscribe(() => {
      if (this.apiError) {
        this.button_clicked = false;
        this.apiError = null;
      }
    });
  }

  get passwordMismatch(): boolean {
    const newPassword = this.passwordForm.get("newPassword")?.value;
    const confirmPassword = this.passwordForm.get("confirmPassword")?.value;
    return newPassword && confirmPassword && newPassword !== confirmPassword;
  }

  checkPassword() {
    const control = this.passwordForm.get("newPassword");
    this.validations = this.passwordService.checkStrength(control?.value || "");
    control?.updateValueAndValidity({ emitEvent: false });
  }

  async onSubmit() {
    if (!this.passwordForm.valid || this.passwordMismatch) return;

    const password = this.passwordForm.get("currentPassword")?.value;
    const newPassword = this.passwordForm.get("newPassword")?.value;

    this.apiError = null;
    this.success = false;
    this.button_clicked = true;
    this.submitting = true;

    const payload = this.setPassword
      ? { regToken: this.regToken, password, newPassword }
      : { password, newPassword };

    const res = await this.gigyaService.execute(this.gigyaService.client.accounts.setAccountInfo, payload);

    if (res.errorCode === 0) {
      this.success = true;
      this.gigyaService.redirectToContinue();
      return;
    }
    this.submitting = false;
    if (res.errorCode === 403042) {
      this.wrongPasswordError = true;
      return;
    }
    this.button_clicked = false;
    this.apiError = this.passwordService.parseError(res, this.errorOverrides);
  }
}
